/** 沙箱任务扩展协议只传展示字段；不传 PID、宿主路径、命令或任务输出。 */
export type BackgroundTask = {
  id: string; type: string; title: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'stopped' | 'unknown';
  canStop: boolean; turnId?: string; toolUseId?: string; startedAt?: number; endedAt?: number;
};
export type BackgroundTaskSnapshot = {
  version: 1; availability: 'available' | 'unsupported' | 'disconnected';
  tasks: BackgroundTask[]; checkedAt: number | null; truncated: boolean;
};
export const BACKGROUND_TASK_ID = /^[A-Za-z0-9_-]{1,128}$/;
const terminal = new Set(['completed', 'failed', 'stopped']);
const snapshotFields = new Set(['version', 'availability', 'tasks', 'checkedAt', 'truncated']);
const taskFields = new Set(['id', 'type', 'title', 'status', 'canStop', 'turnId', 'toolUseId', 'startedAt', 'endedAt']);

/** 将 SDK 的公开任务集合投影为有界历史；注册表移除不等于成功完成。 */
export class BackgroundTaskHistory {
  private readonly rows = new Map<string, BackgroundTask>();
  private truncated = false;

  /** 仅接收当前会话 SDK 的 tasksUpdated/getTasks；turnId 在任务首次出现时固定。 */
  update(value: unknown, turnId?: string): void {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('TASKS_INVALID');
    const seen = new Set<string>();
    for (const [id, raw] of Object.entries(value)) {
      if (!BACKGROUND_TASK_ID.test(id) || !raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      if (item.type === 'local_main' || (item.type === 'local_bash' && item.isBackgrounded === false)) continue;
      if (typeof item.id === 'string' && item.id !== id) continue;
      seen.add(id);
      const old = this.rows.get(id);
      const status = item.status === 'killed' ? 'stopped' : ['pending', 'running', 'completed', 'failed', 'stopped'].includes(String(item.status)) ? item.status as BackgroundTask['status'] : 'unknown';
      const title = [item.title, item.description, item.name].find(text => typeof text === 'string' && text.trim());
      const row: BackgroundTask = { id, type: typeof item.type === 'string' ? item.type.slice(0, 80) : 'unknown',
        title: typeof title === 'string' ? title.slice(0, 500) : id, status, canStop: status === 'running',
        ...(old?.turnId || turnId ? { turnId: old?.turnId ?? turnId } : {}),
        ...(typeof item.toolUseId === 'string' && item.toolUseId.length <= 200 ? { toolUseId: item.toolUseId } : {}),
        ...(typeof item.startTime === 'number' && Number.isFinite(item.startTime) ? { startedAt: item.startTime } : {}),
        ...(typeof item.endTime === 'number' && Number.isFinite(item.endTime) ? { endedAt: item.endTime } : {}) };
      // 后续快照的陈旧 running 不得把已经结束的同 ID 任务复活。
      if (!old || !terminal.has(old.status)) this.rows.set(id, row);
    }
    for (const [id, row] of this.rows) if (!seen.has(id) && !terminal.has(row.status)) this.rows.set(id, { ...row, status: 'unknown', canStop: false });
    while (this.rows.size > 200) {
      const disposable = [...this.rows].find(([, row]) => terminal.has(row.status))?.[0] ?? this.rows.keys().next().value;
      if (disposable) this.rows.delete(disposable);
      this.truncated = true;
    }
  }

  /** SDK 明确确认停止后更新该任务，不修改其他任务或父对话状态。 */
  stopped(taskId: string): void {
    const row = this.rows.get(taskId);
    if (row && !terminal.has(row.status)) this.rows.set(taskId, { ...row, status: 'stopped', canStop: false, endedAt: Date.now() });
  }

  /** 返回独立副本，避免 HTTP/事件订阅者更改 runner 的权威状态。 */
  snapshot(): BackgroundTaskSnapshot {
    return { version: 1, availability: 'available', tasks: [...this.rows.values()].map(row => ({ ...row })), checkedAt: Date.now(), truncated: this.truncated };
  }
}

/** 严格校验外部快照；Host 持久化和 Web 展示共享该边界。 */
export function isBackgroundTaskSnapshot(value: unknown): value is BackgroundTaskSnapshot {
  if (!value || typeof value !== 'object') return false;
  const data = value as BackgroundTaskSnapshot;
  return Object.keys(data).every(key => snapshotFields.has(key)) && data.version === 1 && ['available', 'unsupported', 'disconnected'].includes(data.availability)
    && (data.checkedAt === null || Number.isFinite(data.checkedAt)) && typeof data.truncated === 'boolean'
    && Array.isArray(data.tasks) && data.tasks.length <= 200 && new Set(data.tasks.map(row => row?.id)).size === data.tasks.length
    && data.tasks.every(row => row && Object.keys(row).every(key => taskFields.has(key)) && typeof row.id === 'string' && BACKGROUND_TASK_ID.test(row.id)
      && typeof row.title === 'string' && row.title.length <= 500 && typeof row.type === 'string' && row.type.length <= 80
      && ['pending', 'running', 'completed', 'failed', 'stopped', 'unknown'].includes(row.status) && typeof row.canStop === 'boolean'
      && (!row.canStop || row.status === 'running') && (row.turnId === undefined || typeof row.turnId === 'string' && row.turnId.length <= 200)
      && (row.toolUseId === undefined || typeof row.toolUseId === 'string' && row.toolUseId.length <= 200)
      && (row.startedAt === undefined || Number.isFinite(row.startedAt)) && (row.endedAt === undefined || Number.isFinite(row.endedAt)));
}
